import type { FC } from 'react';
import Cookies from 'js-cookie';
import { useMe } from 'features/login';
import { Button, CircularProgress, ListItem, Menu, MenuItem, styled, Typography } from '@mui/material';
import { useAnchorMenu } from '../../hooks';
import { useLogout } from '../../api/logout';

const UserButtonStyled = styled(Button)(({ theme }) => ({
  width: 32,
  minWidth: 32,
  height: 32,
  minHeight: 32,
  p: 0,
  textAlign: 'center',
  borderRadius: '16px',
  fontSize: theme.typography.body1.fontSize,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
}));

const UserButton: FC = () => {
  const { isOpen, anchor, handleOpenMenu, handleCloseMenu } = useAnchorMenu();
  const { data } = useMe();
  const { mutateAsync: logout, isPending } = useLogout();
  const firstNameInitial = data?.first_name?.[0] ?? 'U';
  const lastNameInitial = data?.last_name?.[0] ?? '';

  const value = `${firstNameInitial}${lastNameInitial}`.toUpperCase();
  const handleSignOut = async () => {
    const r = await logout();
    Cookies.remove('csrftoken', {
      path: '/', domain: window.location.hostname,
    });
    Cookies.remove('csrftoken', {
      path: '/', domain: '.asqq.io',
    });
    Cookies.remove('csrftoken', {
      path: '/', domain: '.user-acquisition.asqq.io',
    });
    if (r.config.baseURL) window.location.href = r.config.baseURL;
  };

  return (
    <>
      <UserButtonStyled
        variant={'text'}
        id="user-nav-button"
        aria-controls={isOpen ? 'user-nav' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        disableElevation
        disableRipple
        disableTouchRipple
        onClick={handleOpenMenu}
      >
        {value}
      </UserButtonStyled>
      <Menu
        id="user-nav"
        slotProps={{
          paper: {
            sx: {
              width: 200,
              ml: 1,
            },
          },
        }}
        elevation={8}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorEl={anchor}
        open={isOpen}
        onClose={!isPending ? handleCloseMenu : undefined}
      >
        <ListItem divider sx={{ px: 2, py: 1 }}>
          <Typography
            sx={(t) => ({
              fontSize: t.typography.body2.fontSize,
              color: t.palette.grey[600],
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            })}
          >
            {data?.email}
          </Typography>
        </ListItem>
        <MenuItem
          onClick={handleSignOut}
          disabled={isPending}
          disableTouchRipple={isPending}
          disableRipple={!isPending}
          sx={{ px: 2, py: 1.5 }}
        >
          {isPending && (
            <CircularProgress sx={{ mr: 1 }} size={16} color="inherit" />
          )}
          <Typography
            sx={(t) => ({
              fontSize: t.typography.body2.fontSize,
            })}
          >
            Log out
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserButton;
